/* eslint-disable no-nested-ternary */
import { RecommenderPopup, Results, RelatedResults, ResourcesBanner } from '@edu-match/components';
import { useEffect, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import prospectScriptConfig from '@core/config/prospectScriptSchoolIds.json';
import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { setContactIdentifiers } from '@core/reducers/contactSlice';
import { selectDcsDegrees, selectDcsCategories, selectDcsSubjects } from '@core/reducers/dcsSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { selectBaseIndex, selectLeadDelivery, selectSubmissions } from '@core/reducers/matchesSlice';
import { selectQueryParam } from '@core/reducers/queryParamsSlice';
import { saveContact } from '@core/services/hub';
import { PageActions } from '@core/services/newRelic';
import PsychBannerTestComponent from '@core/shared/components/PsychBannerTest';
import SponsoredResultsPopover from '@core/shared/components/SponsoredResultsPopover';
import UserExperienceSurvey from '@core/shared/features/UserExperienceSurvey';
import type { VoyagerResult } from '@core/ts/results';
import buildLeadObject, { BuildLeadObjectInputs } from '@core/utils/buildLeadObject';
import cn from '@core/utils/classNames';
import { AddActiveProspectScript } from '@core/utils/scripts/AddActiveProspectScript';

import Loading from '../edu-flow-2/components/Loading/index';
import styles from './EduMatch.module.css';
import DuplicatePopup from './components/DuplicatePopup';
import SubmissionSlideOut from './components/SubmissionSlideOut';
import Submissions from './components/Submissions';
import useGradResultsPageBannerTest from './hooks/useGradResultsPageBannerTest';
import useSetBaseIndex from './hooks/useSetBaseIndex';
import { EduMatchProps } from './types';

const EduMatchUI: FC<EduMatchProps> = ({
  showRecommenderPopup,
  searchClient,
  popupData,
  uniqueMatches,
  popupEventing,
  exactResults = [],
  relatedResults = [],
  showDuplicatePopup,
  loading,
}) => {
  const dispatch = useDispatch();

  const [showUserXpBanner, setShowUserXpBanner] = useState<boolean>(false);
  // Matches State properties
  const baseIndex = useSelector(selectBaseIndex);
  const submissions = useSelector(selectSubmissions);

  const { currentPrograms } = useSelector(selectLeadDelivery);

  // Prospect script schools
  const propsectScriptSchooIdList = prospectScriptConfig;
  const currentProgramsIdList = currentPrograms?.map((cp) => cp?.school?.id);
  const isActiveProspectScriptRequired = currentProgramsIdList?.some((cp) => propsectScriptSchooIdList.includes(cp));

  // DCS State properties
  const dcsDegrees = useSelector(selectDcsDegrees);
  const dcsSubjects = useSelector(selectDcsSubjects);
  const dcsCategories = useSelector(selectDcsCategories);

  const inputs: Record<string, any> = useSelector(selectAllInputs);

  const flags = useFeatureFlags();
  const showPsychBannerTest = !!flags?.psychBannerTest;
  const publisher = useSelector((state) => selectQueryParam(state, 'publisher'));

  // const showPopup = popupData?.data?.length && showRecommenderPopup;

  // User Experience Survey
  const showUserExperienceSurvey =
    flags?.userExperienceSurvey === 'test' && !showRecommenderPopup && submissions.length <= 0 && showUserXpBanner;

  // fetch algolia credentials and init search client
  useSetBaseIndex();

  // evaluate if related programs should be shown or not
  const showRelatedPrograms = exactResults?.length < 52 && dcsDegrees?.length === 1 && dcsSubjects?.length === 1;

  useEffect(() => {
    PageActions.ResultsLoaded({
      resultsPage: 'eduMatch',
      anonymousId: window?._Cohesion.anonymousId,
      sessionId: window?._Cohesion.sessionId,
    });
  }, []);

  useEffect(() => {
    if (!flags?.useContactDb) return;

    saveContact({
      degree: dcsDegrees[0],
      category: dcsCategories[0],
      subject: dcsSubjects[0],
      cohesionSessionId: window?._Cohesion?.sessionId,
      lead: buildLeadObject(inputs as BuildLeadObjectInputs),
      publisher,
    }).then((res) => {
      const payload = {
        contactId: res?.data?.contactId || '',
        transactionId: res?.data?.lastTransaction[0]?.id || 0,
      };
      // on this side of voyager, we're really only concerned about the contact id.  if we have the transaction id, that's nice,
      // but having the contactId is most important for ensuring the contact data was actually saved.
      if (payload.contactId) {
        dispatch(setContactIdentifiers(payload));
      }
    });
  }, [inputs, dcsDegrees, dcsSubjects, dcsCategories, flags]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowUserXpBanner(true);
    }, 120000);

    return () => clearTimeout(timeout);
  }, []);

  const controlResultsBanner = (
    <header className={styles.resultsHeader}>
      {uniqueMatches?.length ? '' : <p>All results for:</p>}
      <h1>{`${dcsDegrees[0]} in ${dcsCategories[0]} programs`}</h1>
    </header>
  );

  const { TestBanner } = useGradResultsPageBannerTest(controlResultsBanner);

  if (loading) return <Loading />;

  return (
    <>
      {/* Handle Trusted Certificate */}
      {isActiveProspectScriptRequired && <AddActiveProspectScript />}
      {showDuplicatePopup && <DuplicatePopup searchClient={searchClient} indexName={baseIndex} />}
      <PsychBannerTestComponent isGrad />
      {showRecommenderPopup && (
        <RecommenderPopup hits={popupData as VoyagerResult[]} eventingOverrides={popupEventing} />
      )}
      {/* exact match results */}

      {/* configure the results */}

      <SubmissionSlideOut />
      {/* app container */}
      <div
        className={cn(
          dcsDegrees?.length === 1 && dcsCategories?.length === 1 && !showPsychBannerTest
            ? styles.eduMatchContainer
            : undefined
        )}
      >
        {/* results header */}
        {/* this header should only be shown if there is just one degree and one category selected */}
        {dcsDegrees?.length === 1 && dcsCategories?.length === 1 && !showPsychBannerTest ? <TestBanner /> : null}

        {/* mobile facets & filters */}
      </div>

      {/* Not showing this if the 'voyager-embedded-recommender' test is ON */}
      <Submissions />

      <div className={cn(exactResults.length > 0 ? styles.eduMatchContainer : undefined)}>
        {uniqueMatches?.length ? (
          <div className={styles.consolidationCopy}>
            <b>Here are your results!</b> The results on this page consist of schools that exactly match or are closely
            related to your interests.
          </div>
        ) : (
          ''
        )}

        {/* results/hits */}
        <section className={styles.resultsContainer}>
          <span>
            <Results relatedResults={relatedResults} results={exactResults} uniqueMatches={uniqueMatches} />
          </span>
        </section>
      </div>

      {/* related results */}
      {showRelatedPrograms && (
        <section className={styles.relatedPrograms}>
          {/* Show related results text when we have some, and conditionally render the copy depending on if we have exact matches */}
          {relatedResults?.length > 0 ? (
            exactResults?.length > 0 ? (
              <p className={styles.relatedLabel}>
                Other programs you might be interested in:
                <SponsoredResultsPopover position="below" />
              </p>
            ) : (
              <p className={cn(styles.relatedLabel, styles.relatedLabelMargin)}>
                Programs you might be interested in:
                <SponsoredResultsPopover position="below" />
              </p>
            )
          ) : (
            <></>
          )}

          {/* added ts-ignore because connectHits has types that come from the algolia library
            his will go away when we change over to hooks
          @ts-ignore */}
          <RelatedResults results={relatedResults} exactMatchesCount={exactResults?.length} />
        </section>
      )}
      <div className={styles.banners}>{showUserExperienceSurvey && <UserExperienceSurvey />}</div>
      {/* show banner after 3 or more submissions */}
      {submissions.length >= 3 && (
        <div className={styles.bannerSection}>
          <ResourcesBanner />
        </div>
      )}
    </>
  );
};

export default EduMatchUI;
